/**
 * OneClick V2 交易 Hook
 *
 * 使用本地 1CT 密钥静默签名（无钱包弹窗），通过 V2 Intent API 提交交易。
 *
 * 与 V1 的关键区别:
 * - V1: 需要创建 1CT 钱包 → OneClickAgent 授权 → salt 签名
 * - V2: 本地密钥签名 → EIP-712 (trader/action/nonce/deadline/fee/actionDataHash) → submit-intent
 */

import type { OpenMarketTradeParams } from '@ui/features/Trade/types';
import { useDataChainId } from '@ui/hooks/datachain';
import {
  ACTION_DATA_ABI_TYPES,
  buildActionData,
  buildTypedData,
  type DecreaseOrderInput,
  type DecreaseOrderUpdateInput,
  getCachedAntiDdosConfig,
  makeDeadline,
  makeNonce,
  OneClickAction,
  type OneClickActionValue,
} from '@ui/services/backend/oneClickV2Service';
import { tradingV2ControllerApi } from '@ui/services/backend/clients/clients';
import {
  SubmitIntentBlockchainEnum,
} from '@ui/services/backend/oneClickServiceGenerated/apis/TradingV2ControllerApi';
import type {
  OneClickIntentRequest,
} from '@ui/services/backend/oneClickServiceGenerated/models';
import { getContractAddress } from '@ui/services/contract/constants';
import type { ChainId } from '@ui/types/chain';
import { useCallback, useState } from 'react';
import { keccak256, type Hex } from 'viem';
import { privateKeyToAccount } from 'viem/accounts';
import { useAccount } from 'wagmi';

import { loadOneClickWalletPrivateKey } from '../oneClickWalletHelper';
import { useOneClickTradingStore } from '../store/useOneClickTradingStore';

// ═══════════════════ 类型 ═══════════════════

type ContractOpenData = Omit<
  OpenMarketTradeParams,
  'value' | 'pythUpdateData' | 'oracleUpdateData'
>;

type SubmitCloseArgs = {
  positionHash: Hex;
  broker?: number;
};

// ═══════════════════ 支持的链 ═══════════════════

function isChainSupported(chainId: ChainId): boolean {
  // V2 supports both mainnet and testnet
  return true;
}

// ═══════════════════ Hook ═══════════════════

export const useOneClickTradingV2 = () => {
  const { address } = useAccount();
  const chainId = useDataChainId();
  const created = useOneClickTradingStore((s) => s.created);
  const enabled = useOneClickTradingStore((s) => s.enabled);

  const [isSubmittingOpen, setIsSubmittingOpen] = useState(false);
  const [isSubmittingClose, setIsSubmittingClose] = useState(false);
  const [isSubmittingBatchClose, setIsSubmittingBatchClose] = useState(false);
  const [isSubmittingLimitOpen, setIsSubmittingLimitOpen] = useState(false);
  const [isSubmittingLimitCancel, setIsSubmittingLimitCancel] = useState(false);
  const [isSubmittingLimitUpdateTpSl, setIsSubmittingLimitUpdateTpSl] = useState(false);
  const [isSubmittingAddMargin, setIsSubmittingAddMargin] = useState(false);
  const [isSubmittingRemoveMargin, setIsSubmittingRemoveMargin] = useState(false);
  const [isSubmittingUpdateTpSl, setIsSubmittingUpdateTpSl] = useState(false);
  const [isSubmittingPartialClose, setIsSubmittingPartialClose] = useState(false);
  const [isSubmittingBatchCreateDecreaseOrders, setIsSubmittingBatchCreateDecreaseOrders] = useState(false);
  const [isSubmittingBatchUpdateDecreaseOrders, setIsSubmittingBatchUpdateDecreaseOrders] = useState(false);
  const [isSubmittingCancelDecreaseOrder, setIsSubmittingCancelDecreaseOrder] = useState(false);

  const isReady = !!address && isChainSupported(chainId as ChainId) && created && enabled;

  /** 获取 Diamond 合约地址 */
  const getDiamondAddress = useCallback((): Hex => {
    const addr = getContractAddress('LeverUp', chainId);
    if (!addr) throw new Error('Diamond contract address not found');
    return addr;
  }, [chainId]);

  /** 签名并提交 Intent（通用） */
  const signAndSubmit = useCallback(
    async (
      action: OneClickActionValue,
      actionValues: unknown[],
    ) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');
      if (!address) throw new Error('Wallet not connected');

      const diamondAddr = getDiamondAddress();

      // 1. Load local 1CT private key for silent signing
      const localPk = await loadOneClickWalletPrivateKey(
        address.toLowerCase(),
      );
      if (!localPk) throw new Error('1CT wallet key not found');

      const signerAccount = privateKeyToAccount(localPk as Hex);

      // 2. 获取 Anti-DDoS 配置
      const { feeToken, antiDdosFee } = getCachedAntiDdosConfig(action);

      // 3. 生成 nonce 和 deadline
      const nonce = makeNonce();
      const deadline = makeDeadline();

      // 4. 构建 actionData 并计算 hash
      const actionData = buildActionData(action, address, actionValues);
      const actionDataHash = keccak256(actionData);

      // 5. 构建 typed data（统一 actionDataHash 结构）并本地签名
      const typedData = buildTypedData(
        action,
        chainId,
        diamondAddr,
        {
          trader: address,
          nonce,
          deadline,
          feeToken,
          antiDdosFee,
        },
        actionDataHash,
      );

      const signature = await signerAccount.signTypedData({
        ...typedData,
      } as any);

      // 6. 提交 intent
      return tradingV2ControllerApi.submitIntent({
        blockchain: SubmitIntentBlockchainEnum.Monad,
        oneClickIntentRequest: {
          trader: address,
          action,
          nonce: nonce.toString(),
          deadline,
          feeToken,
          antiDdosFee: antiDdosFee.toString(),
          actionData,
          signature,
        } satisfies OneClickIntentRequest,
      });
    },
    [address, chainId, getDiamondAddress, isReady],
  );

  // ═══════════════════ 市价开仓 ═══════════════════

  const submitOpenPosition = useCallback(
    async (openData: ContractOpenData) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingOpen(true);
      try {
        return signAndSubmit(OneClickAction.MARKET_OPEN, [
          openData.pairBase,
          openData.isLong,
          openData.tokenIn,
          openData.lvToken,
          openData.amountIn,
          openData.qty,
          openData.price,
          openData.stopLoss,
          openData.takeProfit,
          openData.broker,
          openData.extraFee ?? 0n,
        ]);
      } finally {
        setIsSubmittingOpen(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 市价平仓 ═══════════════════

  const submitClosePosition = useCallback(
    async ({ positionHash, broker = 1 }: SubmitCloseArgs) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingClose(true);
      try {
        return signAndSubmit(OneClickAction.MARKET_CLOSE, [
          positionHash,
          broker,
        ]);
      } finally {
        setIsSubmittingClose(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 批量市价平仓 ═══════════════════

  const submitBatchClosePosition = useCallback(
    async (positionHashes: Hex[], broker?: number) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingBatchClose(true);
      try {
        return signAndSubmit(OneClickAction.BATCH_MARKET_CLOSE, [
          positionHashes,
          broker ?? 1,
        ]);
      } finally {
        setIsSubmittingBatchClose(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 限价开仓 ═══════════════════

  const submitLimitOpen = useCallback(
    async (limitData: ContractOpenData) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingLimitOpen(true);
      try {
        return signAndSubmit(OneClickAction.LIMIT_OPEN, [
          limitData.pairBase,
          limitData.isLong,
          limitData.tokenIn,
          limitData.lvToken,
          limitData.amountIn,
          limitData.qty,
          limitData.price,
          limitData.stopLoss,
          limitData.takeProfit,
          limitData.broker,
          limitData.extraFee ?? 0n,
        ]);
      } finally {
        setIsSubmittingLimitOpen(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 取消限价单 ═══════════════════

  const submitLimitCancel = useCallback(
    async (orderHash: Hex) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingLimitCancel(true);
      try {
        return signAndSubmit(OneClickAction.LIMIT_CANCEL, [orderHash]);
      } finally {
        setIsSubmittingLimitCancel(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 更新限价单 TP/SL ═══════════════════

  const submitLimitUpdateTpSl = useCallback(
    async (orderHash: Hex, takeProfit: bigint, stopLoss: bigint) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingLimitUpdateTpSl(true);
      try {
        return signAndSubmit(OneClickAction.LIMIT_UPDATE_TP_SL, [
          orderHash,
          takeProfit,
          stopLoss,
        ]);
      } finally {
        setIsSubmittingLimitUpdateTpSl(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 添加保证金 ═══════════════════

  const submitAddMargin = useCallback(
    async (positionHash: Hex, tokenIn: Hex, amount: bigint) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingAddMargin(true);
      try {
        return signAndSubmit(OneClickAction.ADD_MARGIN, [
          positionHash,
          tokenIn,
          amount,
        ]);
      } finally {
        setIsSubmittingAddMargin(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 移除保证金 ═══════════════════

  const submitRemoveMargin = useCallback(
    async (positionHash: Hex, lvAmount: bigint) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingRemoveMargin(true);
      try {
        return signAndSubmit(OneClickAction.REMOVE_MARGIN, [
          positionHash,
          lvAmount,
        ]);
      } finally {
        setIsSubmittingRemoveMargin(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 更新仓位 TP/SL ═══════════════════

  const submitUpdateTpSl = useCallback(
    async (positionHash: Hex, takeProfit: bigint, stopLoss: bigint) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingUpdateTpSl(true);
      try {
        return signAndSubmit(OneClickAction.UPDATE_TP_SL, [
          positionHash,
          takeProfit,
          stopLoss,
        ]);
      } finally {
        setIsSubmittingUpdateTpSl(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 部分平仓 ═══════════════════

  const submitPartialClose = useCallback(
    async (positionHash: Hex, closeQty: bigint, broker = 1) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingPartialClose(true);
      try {
        return signAndSubmit(OneClickAction.PARTIAL_CLOSE, [
          positionHash,
          closeQty,
          broker,
        ]);
      } finally {
        setIsSubmittingPartialClose(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 批量创建 TP/SL 委托单 ═══════════════════

  const submitBatchCreateDecreaseOrders = useCallback(
    async (positionHash: Hex, orders: DecreaseOrderInput[]) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingBatchCreateDecreaseOrders(true);
      try {
        return signAndSubmit(
          OneClickAction.BATCH_CREATE_DECREASE_ORDERS,
          [positionHash, orders],
        );
      } finally {
        setIsSubmittingBatchCreateDecreaseOrders(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 批量更新委托单 ═══════════════════

  const submitBatchUpdateDecreaseOrders = useCallback(
    async (updates: DecreaseOrderUpdateInput[]) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingBatchUpdateDecreaseOrders(true);
      try {
        return signAndSubmit(
          OneClickAction.BATCH_UPDATE_DECREASE_ORDERS,
          [updates],
        );
      } finally {
        setIsSubmittingBatchUpdateDecreaseOrders(false);
      }
    },
    [isReady, signAndSubmit],
  );

  // ═══════════════════ 取消委托单 ═══════════════════

  const submitCancelDecreaseOrder = useCallback(
    async (orderHash: Hex) => {
      if (!isReady) throw new Error('OneClick V2 is not ready');

      setIsSubmittingCancelDecreaseOrder(true);
      try {
        return signAndSubmit(OneClickAction.CANCEL_DECREASE_ORDER, [orderHash]);
      } finally {
        setIsSubmittingCancelDecreaseOrder(false);
      }
    },
    [isReady, signAndSubmit],
  );

  return {
    isReady,
    isSubmittingOpen,
    isSubmittingClose,
    isSubmittingBatchClose,
    isSubmittingLimitOpen,
    isSubmittingLimitCancel,
    isSubmittingLimitUpdateTpSl,
    isSubmittingAddMargin,
    isSubmittingRemoveMargin,
    isSubmittingUpdateTpSl,
    isSubmittingPartialClose,
    isSubmittingBatchCreateDecreaseOrders,
    isSubmittingBatchUpdateDecreaseOrders,
    isSubmittingCancelDecreaseOrder,
    sendOpenPosition: submitOpenPosition,
    sendClosePosition: submitClosePosition,
    sendBatchClosePosition: submitBatchClosePosition,
    sendLimitOpen: submitLimitOpen,
    sendLimitCancel: submitLimitCancel,
    sendLimitUpdateTpSl: submitLimitUpdateTpSl,
    sendAddMargin: submitAddMargin,
    sendRemoveMargin: submitRemoveMargin,
    sendUpdateTpSl: submitUpdateTpSl,
    sendPartialClose: submitPartialClose,
    sendBatchCreateDecreaseOrders: submitBatchCreateDecreaseOrders,
    sendBatchUpdateDecreaseOrders: submitBatchUpdateDecreaseOrders,
    sendCancelDecreaseOrder: submitCancelDecreaseOrder,
  };
};
